import React from 'react';
import { Eye, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import type { Document } from '../../../api/documentApi';

interface DocumentValidationWorkflowModalProps {
  open: boolean;
  document: Document | null;
  formData: { notes: string; reason: string };
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
  onFormDataChange: (data: { notes: string; reason: string }) => void;
}

export default function DocumentValidationWorkflowModal({
  open,
  document,
  formData,
  onClose,
  onApprove,
  onReject,
  onFormDataChange,
}: DocumentValidationWorkflowModalProps) {
  if (!document) return null;

  const handleFieldChange = (field: 'notes' | 'reason', value: string) => {
    onFormDataChange({ ...formData, [field]: value });
  };

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Review Dokumen</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-500">Dokumen</p>
              <p className="font-medium">{document.document_name || document.file_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Entitas</p>
              <p className="font-medium">{document.entity_name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Modul</p>
              <Badge>{document.module_type || 'GENERAL'}</Badge>
            </div>
            <div>
              <p className="text-sm text-gray-500">Upload By</p>
              <p className="font-medium">{(document as any).uploaded_by_name || '-'}</p>
            </div>
          </div>

          {document.file_url && (
            <div>
              <a href={document.file_url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full">
                  <Eye className="w-4 h-4 mr-2" />
                  Lihat Dokumen
                </Button>
              </a>
            </div>
          )}

          <div className="space-y-2">
            <Label>Catatan Validasi</Label>
            <Textarea
              value={formData.notes}
              onChange={e => handleFieldChange('notes', e.target.value)}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>Alasan Reject (jika ditolak)</Label>
            <Textarea
              value={formData.reason}
              onChange={e => handleFieldChange('reason', e.target.value)}
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Batal
            </Button>
            <Button variant="destructive" onClick={onReject} disabled={!formData.reason}>
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={onApprove}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
